/**
 * Industry-Standard User Role Detection Hook
 * 
 * Following Netflix/Shopify patterns for role-based conditional data fetching
 * Provides safe, cached role detection for all authenticated users
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface UserRole {
  id: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

export interface UserRoleInfo {
  roles: string[];
  isAdmin: boolean;
  isModerator: boolean;
  isUser: boolean;
  hasAdminAccess: boolean; // admin OR moderator
  isLoading: boolean;
  error: Error | null;
}

/**
 * Safe user role detection hook - Industry Standard Pattern
 * 
 * Used by: Netflix, Shopify, Stripe for role-based UI rendering
 * - Only queries user's own roles (safe for all users)
 * - Cached results for performance
 * - Graceful error handling
 * - No console errors for permission boundaries
 */
export const useUserRole = (): UserRoleInfo => {
  const { user } = useAuth();

  const {
    data: userRoles = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-role', user?.id],
    queryFn: async (): Promise<UserRole[]> => {
      if (!user?.id) {
        return [];
      }

      try {
        // Use real profile service for roles instead of Supabase
        const { realProfileService } = await import('@/services/RealProfileService');
        const roles = await realProfileService.getUserRoles(user.id);

        // Convert string array to UserRole format
        const data = roles.map((role, index) => ({
          id: `${user.id}_${role}_${index}`,
          role: role,
          is_active: true,
          created_at: new Date().toISOString()
        }));

        // If no roles found, assign default user role
        if (!data || data.length === 0) {
          logger.debug('No roles found for user, assigning default user role');
          return [{
            id: `default-${user.id}`,
            role: 'user', 
            is_active: true,
            created_at: new Date().toISOString()
          }];
        }

        return data;
      } catch (error) {
        logger.error('Exception in useUserRole:', error);
        // Always provide graceful fallback
        return [{
          id: `error-fallback-${user.id}`,
          role: 'user',
          is_active: true,
          created_at: new Date().toISOString()
        }];
      }
    },
    enabled: !!user?.id, // Only run when user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes (roles don't change often)
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1, // Single retry for network issues
    retryOnMount: false, // Don't retry on component remount
  });

  // Derive role information
  const roles = userRoles.map(ur => ur.role);
  const isAdmin = roles.includes('admin');
  const isModerator = roles.includes('moderator');
  const isUser = roles.includes('user') || roles.length === 0; // Default to user
  const hasAdminAccess = isAdmin || isModerator;

  return {
    roles,
    isAdmin,
    isModerator, 
    isUser,
    hasAdminAccess,
    isLoading,
    error,
  };
};

/**
 * Convenient hook for admin access checking
 * Used by components that need admin-only functionality
 */
export const useIsAdmin = () => {
  const { isAdmin, isLoading } = useUserRole();
  return { isAdmin, isLoading };
};

/**
 * Convenient hook for admin/moderator access checking  
 * Used by components that need admin OR moderator functionality
 */
export const useHasAdminAccess = () => {
  const { hasAdminAccess, isLoading } = useUserRole();
  return { hasAdminAccess, isLoading };
};

export default useUserRole;